import React, { FC } from 'react';
import { ResponsiveDialog } from '@shared/components/ResponsiveDialog';
import { ButtonV2, Flex, JoyKitThemeProvider, LinkV2, LinkV2Props, TextV2, useDisclosure } from '@withjoy/joykit';
import { ensureHttpsPrefix } from '@shared/utils/urlHelpers';
import { useTranslation } from '@shared/core';
import { Close, WebGlobe } from '@withjoy/joykit/icons';
import { drawerOverrides } from '@shared/components/ResponsiveMenu/ResponsiveMenu.styles';
import { pxToRem } from '@withjoy/joykit/theme';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { StarRating } from '@shared/components';
import { withWindow } from '@shared/utils/withWindow';

// TODO: Fetch data from BE - https://withjoy.atlassian.net/browse/PRNT-2586
const hotelData = {
  name: 'The Godfrey Hotel Hollywood',
  distance: 1.6,
  rating: 3.7,
  link:
    'https://www.booking.com/hotel/in/abad-plaza.en-gb.html?aid=311984&label=abad-plaza-3BD2G8LltuehceRpBdFZ0AS393000067137%3Apl%3Ata%3Ap1%3Ap2%3Aac%3Aap%3Aneg%3Afi%3Atikwd-1917401469%3Alp9149272%3Ali%3Adec%3Adm%3Appccp%3DUmFuZG9tSVYkc2RlIyh9YXORK0YJiVoOxcWODxYDaAA&sid=fe3a20dd62db48759c8f95c92a24814d&dest_id=-2093645&dest_type=city&dist=0&group_adults=5&group_children=0&hapos=1&hpos=1&no_rooms=1&req_adults=5&req_children=0&room1=A%2CA%2CA%2CA%2CA&sb_price_type=total&sr_order=popularity&srepoch=1745559591&srpvid=0ef927d05d250238&type=total&ucfs=1&',
  imageURL: 'https://images.pexels.com/photos/674010/pexels-photo-674010.jpeg?cs=srgb&dl=pexels-anjana-c-169994-674010.jpg&fm=jpg'
};

export const HotelSuggestionDialog: FC<LinkV2Props> = ({ href: hrefProp, onClick, children, ...restProps }) => {
  const { isOpen, onClose, onOpen } = useDisclosure();

  const { t } = useTranslation('joykit');
  const hotelSuggestionDialogTransFn = t('hotelSuggestionDialog');

  const href = ensureHttpsPrefix(hrefProp?.trim() || '');

  const linkProps = {
    textAlign: 'left',
    ...restProps,
    as: 'button',
    onClick: (...args) => {
      if (!href) {
        onClose();
        return;
      }
      onClick?.(...args);
      onOpen();
    }
  } as LinkV2Props;

  const onViewHotelClick = useEventCallback(() => {
    withWindow(window => {
      window.open(hotelData.link, '_blank');
    });
    onClose();
  }, [onClose]);

  const onOpenExternalLinkClick = useEventCallback(() => {
    withWindow(window => {
      window.open(href, '_blank');
    });
    onClose();
  }, [href, onClose]);

  return (
    <>
      <LinkV2 {...linkProps}>{children}</LinkV2>
      <JoyKitThemeProvider>
        <ResponsiveDialog
          isOpen={isOpen}
          onClose={onClose}
          hasCloseButton={false}
          removeBodyPadding={true}
          dialogOverrides={{
            Content: { props: { width: '100%', maxWidth: pxToRem(540), paddingX: 9 } },
            Body: { props: { paddingX: 0, paddingY: 9, paddingBottom: 7 } },
            Footer: { props: { paddingX: { _: 6, sm: 6 }, paddingY: 6 } }
          }}
          drawerOverrides={{
            ...drawerOverrides,
            Content: {
              props: {
                ...drawerOverrides.Content?.props,
                paddingY: 0
              }
            },
            Body: {
              props: {
                ...drawerOverrides.Body?.props,
                paddingX: 6,
                paddingTop: 6,
                paddingBottom: 7
              }
            },
            Footer: { props: { paddingX: 6, paddingY: 6 } }
          }}
          renderFooter={() => (
            <Flex width="100%" flexDirection="column" gap={5}>
              <ButtonV2 variant="solid" size="lg" shape="rounded" intent="neutral" onClick={onViewHotelClick}>
                {hotelSuggestionDialogTransFn.viewHotel()}
              </ButtonV2>
              <ButtonV2 variant="ghost" size="lg" shape="rounded" intent="neutral" onClick={onOpenExternalLinkClick}>
                {hotelSuggestionDialogTransFn.openExternalLink()}
              </ButtonV2>
            </Flex>
          )}
        >
          <Flex flexDirection="column" gap={6} alignItems="flex-start" paddingX={{ _: 0, sm: 6 }}>
            <Flex position="absolute" top={7} right={7} cursor="pointer" display={{ _: 'none', sm: 'flex' }} _hover={{ color: 'mono12' }} _active={{ color: 'mono13' }}>
              <Close size={24} onClick={onClose} />
            </Flex>
            <Flex flexDirection="column" gap={{ _: 1, sm: 3 }} paddingBottom={5} alignItems={{ _: 'flex-start', sm: 'center' }}>
              <TextV2 typographyVariant={{ _: 'hed4', sm: 'hed6' }} color="#020202">
                {hotelSuggestionDialogTransFn.title()}
              </TextV2>
              <TextV2 typographyVariant="body1" color="mono10" textAlign={{ _: 'left', sm: 'center' }}>
                {hotelSuggestionDialogTransFn.description()}
              </TextV2>
            </Flex>

            <Flex flexDirection="row" width="100%" paddingX={6} paddingY={5} gap={4} borderRadius={4} border="1px solid" borderColor="mono3" backgroundColor="white">
              <Flex
                flexShrink={0}
                width={pxToRem(96)}
                height="auto"
                borderRadius={pxToRem(12)}
                background={`url("${hotelData.imageURL}") lightgray 50%`}
                backgroundSize="cover"
                backgroundRepeat="no-repeat"
              />

              <Flex flexDirection="column" gap={3} alignItems="flex-start">
                <TextV2 typographyVariant="hed2" color="mono14">
                  {hotelData.name}
                </TextV2>
                <Flex flexDirection="column" gap={3} alignItems="flex-start" justifyContent="center">
                  <StarRating rating={hotelData.rating} />
                  <TextV2 typographyVariant="button3" fontSize={pxToRem(14)} color="mono10">
                    {hotelSuggestionDialogTransFn.distanceLabel({ distance: hotelData.distance })}
                  </TextV2>
                  <Flex
                    flexDirection="column"
                    paddingX={4}
                    paddingY={2}
                    justifyContent="center"
                    alignItems="center"
                    gap={3}
                    borderRadius={3}
                    border="1px solid"
                    borderColor="#4951EF"
                  >
                    <TextV2 typographyVariant="button3" fontSize={pxToRem(14)} color="#4951EF">
                      {hotelSuggestionDialogTransFn.exclusiveRate()}
                    </TextV2>
                  </Flex>
                </Flex>
              </Flex>
            </Flex>

            <Flex width="100%" flexDirection="column" gap={5} alignItems="flex-start">
              <TextV2 typographyVariant="hed2" color="#000">
                {hotelSuggestionDialogTransFn.externalLink()}
              </TextV2>
              <Flex
                paddingX={5}
                paddingY={3}
                width="100%"
                flexDirection="row"
                alignItems="center"
                justifyContent="flex-start"
                gap={3}
                borderRadius={3}
                border="1px solid"
                borderColor="mono3"
              >
                <WebGlobe size={24} />
                <TextV2 typographyVariant="button3" fontSize={pxToRem(14)} color="mono14" textOverflow="ellipsis" whiteSpace="nowrap" overflow="hidden">
                  {hrefProp}
                </TextV2>
              </Flex>
            </Flex>
          </Flex>
        </ResponsiveDialog>
      </JoyKitThemeProvider>
    </>
  );
};
